import React, { useState, useEffect, useCallback } from 'react';
import { getPorts, createPort, updatePort, deletePort, getPortContainers } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash, Anchor, Package } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const PortsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canDelete = isAdmin || user?.role === 'logistics_manager';
  const canEdit = isAdmin || ['logistics_manager', 'operations'].includes(user?.role);

  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', city: '', notes: '' });

  // Containers drawer
  const [containersDlgOpen, setContainersDlgOpen] = useState(false);
  const [portContainers, setPortContainers] = useState([]);
  const [activePort, setActivePort] = useState(null);
  const [loadingContainers, setLoadingContainers] = useState(false);

  const loadPorts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPorts();
      setPorts(res.data || []);
    } catch (err) {
      toast.error('Failed to load ports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPorts(); }, [loadPorts]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', city: '', notes: '' });
    setDlgOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name || '', city: p.city || '', notes: p.notes || '' });
    setDlgOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Port name is required');
      return;
    }
    try {
      if (editing) {
        await updatePort(editing.id, form);
        toast.success('Port updated');
      } else {
        await createPort(form);
        toast.success('Port created');
      }
      setDlgOpen(false);
      loadPorts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save port');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete port "${p.name}"?`)) return;
    try {
      await deletePort(p.id);
      toast.success('Port deleted');
      loadPorts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const viewContainers = async (p) => {
    setActivePort(p);
    setContainersDlgOpen(true);
    setLoadingContainers(true);
    try {
      const res = await getPortContainers(p.id);
      setPortContainers(res.data || []);
    } catch (err) {
      toast.error('Failed to load containers');
      setPortContainers([]);
    } finally {
      setLoadingContainers(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Anchor weight="duotone" className="w-7 h-7 text-primary" />
            Dédouanement (Ports)
          </h1>
          <p className="text-sm text-muted-foreground">Customs clearance ports — assign containers and cars by port of arrival.</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="add-port-btn">
            <Plus weight="bold" className="w-4 h-4 mr-1" /> Add Port
          </Button>
        )}
      </div>

      <div className="bg-white border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : ports.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No ports yet. {canEdit && 'Click "Add Port" to create one (e.g. Alger, Oran, Béjaïa, Skikda).'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">City</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ports.map(p => (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`port-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium">{p.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{p.city || '-'}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{p.notes || '-'}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => viewContainers(p)} data-testid={`port-containers-${p.id}`}>
                        <Package className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`port-edit-${p.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(p)} data-testid={`port-delete-${p.id}`}>
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Port' : 'New Port'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Alger, Oran, Béjaïa" data-testid="port-name-input" autoFocus />
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Algiers" data-testid="port-city-input" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="port-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="port-save-btn">{editing ? 'Save' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Containers Dialog */}
      <Dialog open={containersDlgOpen} onOpenChange={setContainersDlgOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Containers at {activePort?.name}</DialogTitle>
          </DialogHeader>
          {loadingContainers ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : portContainers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No containers assigned to this port yet.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {portContainers.map(c => (
                <div key={c.container_number} className="border rounded p-3" data-testid={`port-container-${c.container_number}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm font-bold">{c.container_number}</span>
                    <Badge variant="secondary">{c.car_count} car{c.car_count > 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="space-y-0.5">
                    {c.cars.map(car => (
                      <div key={car.id} className="text-xs text-muted-foreground flex justify-between">
                        <span>{car.brand} {car.model} {car.color && `· ${car.color}`}</span>
                        <span className="font-mono">{car.vin || '-'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortsPage;
